import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/PurchaseAPI/transferRecieptMultiAPI';

const getErrorMessage = (err, fallback) =>
    err?.ErrorDetails?.ErrorMessage || err?.Message || err?.message || fallback;

// ── Async Thunks ─────────────────────────────────────────────────────────────

export const fetchMultiGrid = createAsyncThunk(
    'transferRecieptMulti/fetchGrid',
    async ({ roleId, uid }, { rejectWithValue }) => {
        try {
            const res = await api.getTransferRecieptMultiGridDetails(roleId, uid);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load the Multi MRR queue'));
        }
    }
);

export const fetchMultiItemDetails = createAsyncThunk(
    'transferRecieptMulti/fetchItemDetails',
    async ({ mrrno, pono, potype }, { rejectWithValue }) => {
        try {
            const res = await api.getGridTransferRecieptMultiData(mrrno, pono, potype);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load item details'));
        }
    }
);

// Success format is "Submitted,<MRRNo>,<DownloadExt>" — DownloadExt doubles as the S3 upload
// key for the attached PDF, same convention as the single-MRR page.
export const submitMultiVendorReciept = createAsyncThunk(
    'transferRecieptMulti/submitVendor',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.saveVendorTransferRecieptMulti(payload);
            const raw = res?.Data || '';
            const [status, mrrNo, downloadExt] = raw.split(',');
            if (status !== 'Submitted') {
                return rejectWithValue(raw || 'Failed to submit');
            }
            return { mrrNo, downloadExt };
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to submit'));
        }
    }
);

// ── Slice ────────────────────────────────────────────────────────────────────

const initialState = {
    gridItems: [],
    itemDetails: [],
    submitResult: null,
    loading: {
        grid: false,
        itemDetails: false,
        submit: false,
    },
    errors: {
        grid: null,
        itemDetails: null,
        submit: null,
    },
};

const transferRecieptMultiSlice = createSlice({
    name: 'transferRecieptMulti',
    initialState,
    reducers: {
        clearMultiItemDetails: (s) => { s.itemDetails = []; s.errors.itemDetails = null; },
        clearMultiSubmitResult: (s) => { s.submitResult = null; s.errors.submit = null; },
        resetTransferRecieptMulti: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchMultiGrid.pending,   (s) => { s.loading.grid = true; s.errors.grid = null; })
            .addCase(fetchMultiGrid.fulfilled, (s, a) => { s.loading.grid = false; s.gridItems = a.payload; })
            .addCase(fetchMultiGrid.rejected,  (s, a) => { s.loading.grid = false; s.errors.grid = a.payload; });

        builder
            .addCase(fetchMultiItemDetails.pending,   (s) => { s.loading.itemDetails = true; s.errors.itemDetails = null; })
            .addCase(fetchMultiItemDetails.fulfilled, (s, a) => { s.loading.itemDetails = false; s.itemDetails = a.payload; })
            .addCase(fetchMultiItemDetails.rejected,  (s, a) => { s.loading.itemDetails = false; s.errors.itemDetails = a.payload; s.itemDetails = []; });

        builder
            .addCase(submitMultiVendorReciept.pending,   (s) => { s.loading.submit = true; s.errors.submit = null; s.submitResult = null; })
            .addCase(submitMultiVendorReciept.fulfilled, (s, a) => { s.loading.submit = false; s.submitResult = a.payload; })
            .addCase(submitMultiVendorReciept.rejected,  (s, a) => { s.loading.submit = false; s.errors.submit = a.payload; });
    },
});

export const {
    clearMultiItemDetails,
    clearMultiSubmitResult,
    resetTransferRecieptMulti,
} = transferRecieptMultiSlice.actions;

// ── Selectors ────────────────────────────────────────────────────────────────
export const selectTRMGridItems    = (s) => s.transferRecieptMulti.gridItems;
export const selectTRMItemDetails  = (s) => s.transferRecieptMulti.itemDetails;
export const selectTRMSubmitResult = (s) => s.transferRecieptMulti.submitResult;
export const selectTRMLoading      = (s) => s.transferRecieptMulti.loading;
export const selectTRMErrors       = (s) => s.transferRecieptMulti.errors;

export default transferRecieptMultiSlice.reducer;
